import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { 
  Users, ListChecks, Clock, CheckCircle, AlertCircle, User, Briefcase,
  Loader2, LayoutGrid, List, Calendar, GanttChart, Search,
  ChevronLeft, ChevronRight, CheckSquare, Square, Plus, X, MessageSquare
} from 'lucide-react';
import TaskComments, { CollaboratorsList } from '../components/TaskComments';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const PRIORITY_COLORS = {
  'Low': 'bg-gray-100 text-gray-800',
  'Medium': 'bg-blue-100 text-blue-800',
  'High': 'bg-orange-100 text-orange-800',
  'Urgent': 'bg-red-100 text-red-800'
};

const PRIORITY_BORDER = {
  'Low': 'border-l-gray-400',
  'Medium': 'border-l-blue-500',
  'High': 'border-l-orange-500',
  'Urgent': 'border-l-red-500'
};

const STATUS_COLORS = {
  'To Do': 'bg-slate-200 text-slate-700',
  'In Progress': 'bg-blue-100 text-blue-800',
  'Completed': 'bg-green-100 text-green-800',
  'On Hold': 'bg-yellow-100 text-yellow-800',
  'Cancelled': 'bg-red-100 text-red-800'
};

const STATUS_DOT = {
  'To Do': 'bg-slate-400', 'Upcoming': 'bg-slate-400',
  'In Progress': 'bg-blue-500', 'Completed': 'bg-green-500',
  'On Hold': 'bg-yellow-500', 'Cancelled': 'bg-red-400'
};

const KANBAN_COLUMNS = ['To Do', 'In Progress', 'On Hold', 'Completed'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const TeamCalendar = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamOverview, setTeamOverview] = useState({ subordinates: [], summary: {} });
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberTasks, setMemberTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [viewMode, setViewMode] = useState('kanban');
  const [filterTab, setFilterTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [calendarDate, setCalendarDate] = useState(new Date());
  // Task detail
  const [selectedTask, setSelectedTask] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [employees, setEmployees] = useState([]);

  const { getAuthHeaders, currentOffice, isOfficeAdmin, user } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => { fetchTeamData(); fetchEmployees(); }, [currentOffice]);
  useEffect(() => { if (selectedMember) fetchMemberTasks(selectedMember.id); }, [selectedMember]);

  const fetchTeamData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      const [membersRes, overviewRes] = await Promise.all([
        fetch(`${API_URL}/api/tasks/subordinates?include_cross_office=false`, { headers }),
        fetch(`${API_URL}/api/tasks/team-overview`, { headers })
      ]);
      if (membersRes.ok) setTeamMembers(await membersRes.json());
      if (overviewRes.ok) setTeamOverview(await overviewRes.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchEmployees = async () => {
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      const res = await fetch(`${API_URL}/api/employees/colleagues`, { headers });
      if (res.ok) setEmployees(await res.json());
    } catch (err) { console.error(err); }
  };

  const fetchMemberTasks = async (employeeId) => {
    setLoadingTasks(true);
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      const res = await fetch(`${API_URL}/api/tasks/subordinate/${employeeId}/tasks`, { headers });
      if (res.ok) setMemberTasks(await res.json());
    } catch (err) { console.error(err); }
    finally { setLoadingTasks(false); }
  };

  const handleUpdateTask = async (taskId, updates) => {
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      const res = await fetch(`${API_URL}/api/tasks/${taskId}`, {
        method: 'PUT', headers, body: JSON.stringify(updates)
      });
      if (res.ok) {
        const updated = await res.json();
        setMemberTasks(prev => prev.map(t => t.id === taskId ? updated : t));
        if (selectedTask?.id === taskId) setSelectedTask(updated);
        return true;
      }
    } catch (err) { console.error(err); }
    return false;
  };

  const handleAddChecklistItem = async () => {
    if (!newChecklistItem.trim() || !selectedTask) return;
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      const res = await fetch(`${API_URL}/api/tasks/${selectedTask.id}/checklist`, {
        method: 'POST', headers, body: JSON.stringify({ text: newChecklistItem })
      });
      if (res.ok) {
        const updated = await res.json();
        setMemberTasks(prev => prev.map(t => t.id === selectedTask.id ? updated : t));
        setSelectedTask(updated);
        setNewChecklistItem('');
      }
    } catch (err) { toast.error('Failed to add checklist item'); }
  };

  const handleToggleChecklistItem = async (itemId) => {
    if (!selectedTask) return;
    const item = selectedTask.checklist?.find(i => i.id === itemId);
    if (!item) return;
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      const res = await fetch(`${API_URL}/api/tasks/${selectedTask.id}/checklist/${itemId}`, {
        method: 'PUT', headers, body: JSON.stringify({ completed: !item.completed })
      });
      if (res.ok) {
        const updated = await res.json();
        setMemberTasks(prev => prev.map(t => t.id === selectedTask.id ? updated : t));
        setSelectedTask(updated);
      }
    } catch (err) { toast.error('Failed to update item'); }
  };

  const handleDeleteChecklistItem = async (itemId) => {
    if (!selectedTask) return;
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      const res = await fetch(`${API_URL}/api/tasks/${selectedTask.id}/checklist/${itemId}`, {
        method: 'DELETE', headers
      });
      if (res.ok) {
        const updated = await res.json();
        setMemberTasks(prev => prev.map(t => t.id === selectedTask.id ? updated : t));
        setSelectedTask(updated);
      }
    } catch (err) { toast.error('Failed to delete item'); }
  };

  // Filter tasks
  const filteredTasks = useMemo(() => {
    let tasks = memberTasks;
    const memberId = selectedMember?.user_id;
    if (filterTab === 'assigned') tasks = tasks.filter(t => t.assigned_to === memberId);
    else if (filterTab === 'created') tasks = tasks.filter(t => t.created_by_id === memberId);
    else if (filterTab === 'collaboration') tasks = tasks.filter(t => t.task_type === 'collaboration');
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      tasks = tasks.filter(t => t.title?.toLowerCase().includes(q) || t.description?.toLowerCase().includes(q));
    }
    return tasks;
  }, [memberTasks, filterTab, searchQuery, selectedMember]);

  const openTask = (task) => { setSelectedTask(task); setShowDetailModal(true); };

  // Calendar helpers
  const calendarDays = useMemo(() => {
    const y = calendarDate.getFullYear(), m = calendarDate.getMonth();
    const first = new Date(y, m, 1), last = new Date(y, m + 1, 0);
    const days = [];
    const prev = new Date(y, m, 0).getDate();
    for (let i = first.getDay() - 1; i >= 0; i--) days.push({ date: new Date(y, m - 1, prev - i), cur: false });
    for (let i = 1; i <= last.getDate(); i++) days.push({ date: new Date(y, m, i), cur: true });
    while (days.length < 42) days.push({ date: new Date(y, m + 1, days.length - last.getDate() - first.getDay() + 1), cur: false });
    return days;
  }, [calendarDate]);

  const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

  // Timeline helpers
  const timelineTasks = useMemo(() => {
    return [...filteredTasks].filter(t => t.due_date).sort((a, b) => (a.due_date > b.due_date ? 1 : -1));
  }, [filteredTasks]);

  const { summary } = teamOverview;

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>;

  // === TASK CARD (shared across views) ===
  const TaskCard = ({ task, compact }) => (
    <div
      onClick={() => openTask(task)}
      className={`p-3 border rounded-lg cursor-pointer hover:shadow-md transition-all border-l-4 ${PRIORITY_BORDER[task.priority] || 'border-l-gray-400'} bg-white`}
      data-testid={`task-card-${task.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <h4 className="font-medium text-sm text-gray-900 line-clamp-2">{task.title}</h4>
        {task.comments?.length > 0 && <MessageSquare className="h-3.5 w-3.5 text-gray-400 shrink-0" />}
      </div>
      {!compact && task.description && <p className="text-xs text-gray-500 mt-1 line-clamp-1">{task.description}</p>}
      <div className="flex flex-wrap gap-1.5 mt-2">
        <Badge className={`text-[10px] px-1.5 py-0 ${STATUS_COLORS[task.status] || 'bg-gray-100'}`}>{task.status}</Badge>
        <Badge variant="outline" className="text-[10px] px-1.5 py-0">{task.priority}</Badge>
        {task.due_date && (
          <span className={`text-[10px] flex items-center gap-0.5 ${task.is_overdue ? 'text-red-600 font-medium' : 'text-gray-400'}`}>
            <Clock className="h-3 w-3" />{new Date(task.due_date).toLocaleDateString()}
          </span>
        )}
      </div>
      {task.checklist?.length > 0 && (
        <div className="mt-1.5 flex items-center gap-1 text-[10px] text-gray-400">
          <CheckSquare className="h-3 w-3" />
          {task.checklist.filter(i => i.completed).length}/{task.checklist.length}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="my-team-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">My Team</h1>
        <p className="text-gray-600">
          {isOfficeAdmin?.() ? "View and manage tasks of all employees in your office" : "View and manage tasks of your direct reports"}
        </p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Team Members', value: teamMembers.length, icon: Users, color: 'blue' },
          { label: 'Total Tasks', value: summary.total_tasks || 0, icon: ListChecks, color: 'purple' },
          { label: 'Pending', value: summary.pending || 0, icon: Clock, color: 'yellow' },
          { label: 'Overdue', value: summary.overdue || 0, icon: AlertCircle, color: 'red' },
          { label: 'Completed', value: summary.completed || 0, icon: CheckCircle, color: 'green' },
        ].map((s, i) => (
          <Card key={i}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className={`p-2 bg-${s.color}-100 rounded-lg`}>
                <s.icon className={`h-5 w-5 text-${s.color}-600`} />
              </div>
              <div>
                <p className="text-xs text-gray-500">{s.label}</p>
                <p className={`text-xl font-bold ${s.color === 'red' && s.value > 0 ? 'text-red-600' : ''}`}>{s.value}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Team Members Sidebar */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><Users className="h-4 w-4" />Team Members</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-[600px] overflow-y-auto">
              {teamMembers.length === 0 ? (
                <div className="p-6 text-center text-gray-500"><Users className="h-8 w-8 mx-auto mb-2 opacity-50" /><p>No team members</p></div>
              ) : (
                <div className="divide-y">
                  {teamMembers.map((m) => {
                    const stats = teamOverview.subordinates?.find(s => s.id === m.id)?.tasks || {};
                    return (
                      <div key={m.id}
                        className={`p-3 cursor-pointer hover:bg-gray-50 transition-colors ${selectedMember?.id === m.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''}`}
                        onClick={() => { setSelectedMember(m); setFilterTab('all'); setSearchQuery(''); }}
                        data-testid={`team-member-${m.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="h-9 w-9 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-sm font-medium shrink-0">
                            {m.name?.charAt(0) || 'E'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm text-gray-900 truncate">{m.name}</p>
                            <p className="text-xs text-gray-500 truncate">{m.job_title || 'Employee'}</p>
                          </div>
                        </div>
                        {stats.total > 0 && (
                          <div className="mt-1.5 flex gap-2 text-[11px] pl-12">
                            <span className="text-yellow-600">{stats.pending || 0} pending</span>
                            {stats.overdue > 0 && <span className="text-red-600 font-medium">{stats.overdue} overdue</span>}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Main Content */}
        <div className="lg:col-span-3 space-y-4">
          {!selectedMember ? (
            <Card>
              <CardContent className="p-12 text-center">
                <User className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-medium text-gray-900">Select a Team Member</h3>
                <p className="text-gray-500 mt-1">Click on a team member to view their tasks</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Member Header */}
              <Card>
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-11 w-11 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-lg font-medium">
                      {selectedMember.name?.charAt(0)}
                    </div>
                    <div>
                      <h2 className="font-semibold text-gray-900">{selectedMember.name}</h2>
                      <p className="text-sm text-gray-500 flex items-center gap-1">
                        <Briefcase className="h-3.5 w-3.5" />
                        {selectedMember.job_title || 'Employee'}
                        {selectedMember.department && ` · ${selectedMember.department}`}
                      </p>
                    </div>
                    {loadingTasks && <Loader2 className="h-4 w-4 animate-spin text-blue-500 ml-2" />}
                  </div>
                  <div className="flex items-center gap-1">
                    {[
                      { mode: 'kanban', icon: LayoutGrid, label: 'Kanban' },
                      { mode: 'list', icon: List, label: 'List' },
                      { mode: 'calendar', icon: Calendar, label: 'Calendar' },
                      { mode: 'timeline', icon: GanttChart, label: 'Timeline' },
                    ].map(v => (
                      <Button key={v.mode} variant={viewMode === v.mode ? 'default' : 'ghost'} size="sm"
                        onClick={() => setViewMode(v.mode)} title={v.label} data-testid={`view-${v.mode}`}>
                        <v.icon className="h-4 w-4" />
                      </Button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Filters */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Tabs value={filterTab} onValueChange={setFilterTab} className="flex-1">
                  <TabsList className="h-9">
                    <TabsTrigger value="all" className="text-xs" data-testid="filter-all">All ({memberTasks.length})</TabsTrigger>
                    <TabsTrigger value="assigned" className="text-xs" data-testid="filter-assigned">Assigned</TabsTrigger>
                    <TabsTrigger value="created" className="text-xs" data-testid="filter-created">Created By</TabsTrigger>
                    <TabsTrigger value="collaboration" className="text-xs" data-testid="filter-collab">Collaboration</TabsTrigger>
                  </TabsList>
                </Tabs>
                <div className="relative w-full sm:w-56">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                  <Input placeholder="Search tasks..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                    className="pl-9 h-9 text-sm" data-testid="search-tasks" />
                </div>
              </div>

              {/* Views */}
              {viewMode === 'kanban' && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  {KANBAN_COLUMNS.map(col => {
                    const colTasks = filteredTasks.filter(t => t.status === col);
                    return (
                      <div key={col} className="bg-gray-50 rounded-lg p-2.5">
                        <div className="flex items-center gap-2 mb-2.5 px-1">
                          <div className={`w-2 h-2 rounded-full ${STATUS_DOT[col]}`} />
                          <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{col}</span>
                          <Badge variant="outline" className="ml-auto text-[10px] h-5">{colTasks.length}</Badge>
                        </div>
                        <div className="space-y-2 max-h-[400px] overflow-y-auto">
                          {colTasks.length === 0 ? (
                            <p className="text-xs text-gray-400 text-center py-4">No tasks</p>
                          ) : colTasks.map(task => <TaskCard key={task.id} task={task} />)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {viewMode === 'list' && (
                <Card>
                  <CardContent className="p-0">
                    {filteredTasks.length === 0 ? (
                      <div className="text-center py-8 text-gray-500"><ListChecks className="h-8 w-8 mx-auto mb-2 opacity-50" /><p>No tasks</p></div>
                    ) : (
                      <div className="divide-y">
                        {filteredTasks.map(task => (
                          <div key={task.id} onClick={() => openTask(task)}
                            className={`p-3 cursor-pointer hover:bg-gray-50 transition-colors border-l-4 ${PRIORITY_BORDER[task.priority] || 'border-l-gray-400'}`}
                            data-testid={`list-task-${task.id}`}>
                            <div className="flex items-center gap-3">
                              <div className="flex-1 min-w-0">
                                <h4 className="font-medium text-sm text-gray-900 truncate">{task.title}</h4>
                                {task.description && <p className="text-xs text-gray-500 truncate mt-0.5">{task.description}</p>}
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <Badge className={`text-[10px] ${STATUS_COLORS[task.status]}`}>{task.status}</Badge>
                                {task.due_date && (
                                  <span className={`text-[10px] ${task.is_overdue ? 'text-red-600' : 'text-gray-400'}`}>
                                    {new Date(task.due_date).toLocaleDateString()}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {viewMode === 'calendar' && (
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-4">
                      <Button variant="outline" size="sm" onClick={() => setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() - 1, 1))}>
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <h3 className="font-semibold">{MONTHS[calendarDate.getMonth()]} {calendarDate.getFullYear()}</h3>
                      <Button variant="outline" size="sm" onClick={() => setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() + 1, 1))}>
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="border rounded-lg overflow-hidden">
                      <div className="grid grid-cols-7 bg-gray-50">
                        {WEEKDAYS.map(d => <div key={d} className="p-2 text-center text-xs font-medium text-gray-500 border-b">{d}</div>)}
                      </div>
                      <div className="grid grid-cols-7">
                        {calendarDays.map((day, i) => {
                          const ds = fmt(day.date);
                          const dayTasks = filteredTasks.filter(t => t.due_date === ds);
                          const isToday = day.date.toDateString() === new Date().toDateString();
                          return (
                            <div key={i} className={`min-h-[80px] p-1 border-b border-r text-xs ${!day.cur ? 'bg-gray-50 text-gray-400' : ''} ${isToday ? 'bg-blue-50' : ''}`}>
                              <div className={`font-medium mb-0.5 ${isToday ? 'text-blue-600' : ''}`}>{day.date.getDate()}</div>
                              {dayTasks.slice(0, 2).map(t => (
                                <div key={t.id} onClick={() => openTask(t)}
                                  className="bg-blue-100 text-blue-800 rounded px-1 py-0.5 mb-0.5 truncate cursor-pointer hover:bg-blue-200 text-[10px]">{t.title}</div>
                              ))}
                              {dayTasks.length > 2 && <div className="text-[10px] text-gray-500 px-1">+{dayTasks.length - 2} more</div>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {viewMode === 'timeline' && (
                <Card>
                  <CardContent className="p-4">
                    {timelineTasks.length === 0 ? (
                      <div className="text-center py-8 text-gray-500"><GanttChart className="h-8 w-8 mx-auto mb-2 opacity-50" /><p>No tasks with due dates</p></div>
                    ) : (
                      <div className="relative">
                        <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />
                        <div className="space-y-4">
                          {timelineTasks.map((task, i) => {
                            const prevDate = i > 0 ? timelineTasks[i-1].due_date : null;
                            const showDate = task.due_date !== prevDate;
                            return (
                              <div key={task.id}>
                                {showDate && (
                                  <div className="flex items-center gap-3 mb-2 ml-1">
                                    <div className={`w-2.5 h-2.5 rounded-full border-2 ${task.is_overdue ? 'bg-red-500 border-red-300' : 'bg-blue-500 border-blue-300'} z-10`} />
                                    <span className={`text-xs font-semibold ${task.is_overdue ? 'text-red-600' : 'text-gray-600'}`}>
                                      {new Date(task.due_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                                      {task.is_overdue && ' (Overdue)'}
                                    </span>
                                  </div>
                                )}
                                <div className="ml-10" onClick={() => openTask(task)}>
                                  <TaskCard task={task} compact />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      </div>

      {/* Task Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={(open) => { setShowDetailModal(open); if (!open) setSelectedTask(null); }}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="task-detail-modal">
          {selectedTask && (
            <form onSubmit={async (e) => {
              e.preventDefault();
              const ok = await handleUpdateTask(selectedTask.id, {
                title: selectedTask.title, description: selectedTask.description,
                priority: selectedTask.priority, status: selectedTask.status, due_date: selectedTask.due_date
              });
              if (ok) { toast.success('Task updated'); setShowDetailModal(false); setSelectedTask(null); }
            }}>
              <DialogHeader>
                <div className="flex items-center gap-2">
                  <Input value={selectedTask.title}
                    onChange={e => setSelectedTask({...selectedTask, title: e.target.value})}
                    className="text-lg font-semibold border-0 border-b-2 border-transparent focus:border-blue-400 rounded-none px-0 focus-visible:ring-0"
                    data-testid="edit-task-title-inline" />
                  {selectedTask.is_overdue && <Badge className="bg-red-100 text-red-800 shrink-0">Overdue</Badge>}
                </div>
              </DialogHeader>

              <div className="space-y-4 mt-4">
                {/* Task type badges */}
                <div className="flex items-center gap-2 flex-wrap">
                  {selectedTask.task_type === 'assigned' && <Badge className="bg-blue-50 text-blue-700 text-xs">Assigned</Badge>}
                  {selectedTask.task_type === 'collaboration' && <Badge className="bg-purple-50 text-purple-700 text-xs">Collaboration</Badge>}
                  {selectedTask.task_type === 'self' && <Badge className="bg-gray-50 text-gray-700 text-xs">Self</Badge>}
                  {selectedTask.assigned_to_name && (
                    <span className="text-xs text-gray-500 flex items-center gap-1"><User className="h-3 w-3" />{selectedTask.assigned_to_name}</span>
                  )}
                </div>

                <div>
                  <Label className="text-gray-500 text-xs">Description</Label>
                  <Textarea value={selectedTask.description || ''}
                    onChange={e => setSelectedTask({...selectedTask, description: e.target.value})}
                    placeholder="Add a description..." className="mt-1 min-h-[80px] resize-none" rows={3}
                    data-testid="edit-task-description-inline" />
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-gray-500 text-xs">Status</Label>
                    <Select value={selectedTask.status} onValueChange={v => {
                      setSelectedTask({...selectedTask, status: v});
                      handleUpdateTask(selectedTask.id, { status: v });
                    }}>
                      <SelectTrigger className="mt-1" data-testid="edit-status-inline"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {['To Do', 'In Progress', 'Completed', 'On Hold'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-gray-500 text-xs">Priority</Label>
                    <Select value={selectedTask.priority} onValueChange={v => setSelectedTask({...selectedTask, priority: v})}>
                      <SelectTrigger className="mt-1" data-testid="edit-priority-inline"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {['Low', 'Medium', 'High', 'Urgent'].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-gray-500 text-xs">Due Date</Label>
                    <Input type="date" value={selectedTask.due_date?.split('T')[0] || ''}
                      onChange={e => setSelectedTask({...selectedTask, due_date: e.target.value})}
                      className="mt-1" data-testid="edit-duedate-inline" />
                  </div>
                </div>

                {/* Checklist */}
                <div className="border-t pt-4">
                  <Label className="flex items-center gap-2 text-gray-700 mb-2">
                    <CheckSquare className="w-4 h-4" /> Checklist
                    {selectedTask.checklist?.length > 0 && (
                      <span className="text-xs text-gray-500">({selectedTask.checklist.filter(i => i.completed).length}/{selectedTask.checklist.length})</span>
                    )}
                  </Label>
                  {selectedTask.checklist?.length > 0 && (
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mb-3">
                      <div className="h-full bg-green-500 transition-all"
                        style={{ width: `${(selectedTask.checklist.filter(i => i.completed).length / selectedTask.checklist.length) * 100}%` }} />
                    </div>
                  )}
                  <div className="space-y-1 mb-2">
                    {selectedTask.checklist?.map(item => (
                      <div key={item.id} className="flex items-center gap-2 group p-1.5 rounded hover:bg-gray-50">
                        <button type="button" onClick={() => handleToggleChecklistItem(item.id)} className="shrink-0">
                          {item.completed ? <CheckSquare className="w-4 h-4 text-green-500" /> : <Square className="w-4 h-4 text-gray-400" />}
                        </button>
                        <span className={`flex-1 text-sm ${item.completed ? 'line-through text-gray-400' : ''}`}>{item.text}</span>
                        <button type="button" onClick={() => handleDeleteChecklistItem(item.id)}
                          className="opacity-0 group-hover:opacity-100 p-0.5 hover:bg-red-50 rounded">
                          <X className="w-3.5 h-3.5 text-red-500" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input value={newChecklistItem} onChange={e => setNewChecklistItem(e.target.value)}
                      placeholder="Add a sub-task..." className="text-sm"
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddChecklistItem(); } }} />
                    <Button type="button" variant="outline" size="icon" onClick={handleAddChecklistItem} disabled={!newChecklistItem.trim()}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Collaborators */}
                <div className="border-t pt-4">
                  <CollaboratorsList task={selectedTask} employees={employees.length > 0 ? employees : teamMembers} />
                  <div className="mt-2">
                    <Label className="text-gray-500 text-xs mb-1 block">Add Collaborator</Label>
                    <Select onValueChange={(v) => {
                      const currentCollabs = (selectedTask.collaborators || []).map(c => c.user_id);
                      if (!currentCollabs.includes(v)) {
                        handleUpdateTask(selectedTask.id, { collaborators: [...currentCollabs, v] });
                      }
                    }}>
                      <SelectTrigger className="h-9" data-testid="add-collaborator-select">
                        <SelectValue placeholder="Select team member..." />
                      </SelectTrigger>
                      <SelectContent>
                        {(employees.length > 0 ? employees : teamMembers).filter(emp => {
                          const existingIds = (selectedTask.collaborators || []).map(c => c.user_id);
                          return !existingIds.includes(emp.id) && !existingIds.includes(emp.user_id) && emp.id !== selectedTask.assigned_to;
                        }).map(emp => (
                          <SelectItem key={emp.id} value={emp.user_id || emp.id}>{emp.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Comments */}
                <TaskComments taskId={selectedTask.id} task={selectedTask}
                  collaborators={selectedTask.collaborators || []} employees={employees.length > 0 ? employees : teamMembers} />

                {selectedTask.created_by_name && (
                  <div className="text-xs text-gray-500 pt-2 border-t">
                    Created by {selectedTask.created_by_name}
                    {selectedTask.created_at && ` on ${new Date(selectedTask.created_at).toLocaleDateString()}`}
                  </div>
                )}
              </div>

              <DialogFooter className="mt-4">
                <Button type="button" variant="outline" onClick={() => setShowDetailModal(false)}>Close</Button>
                <Button type="submit" data-testid="save-task-button">
                  <CheckCircle className="h-4 w-4 mr-2" />Save Changes
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamCalendar;
